import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { Fragment, useEffect, useState } from 'react';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import type { Step } from '@sorare/core/src/components/StepperDialog';
import { StepperDialog } from '@sorare/core/src/components/StepperDialog';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { isType, isTypeFn } from '@sorare/core/src/lib/gql/index.ts';
import { RevealPackCardsDialogFromReward } from '@sorare/marketplace/src/components/commonCard/RevealPackCardsDialogFromReward';

import { ChooseYourRewardStory } from '#src/components/missions/DailyTask/DecisivePlayerPickerTaskStatus/ChooseYourRewardStory/index.tsx';
import { ladderPointsSteps } from '#src/components/missions/stories/LadderPointsStory/ladderPointsSteps.tsx';
import { RewardUnlockedStoryLoading } from '#src/components/missions/stories/RewardUnlockedStory/RewardUnlockedStoryLoading/index.tsx';
import type { RewardUnlockedStory_reward } from '#src/components/missions/stories/RewardUnlockedStory/__generated__/index.graphql.ts';
import { RewardUnlockedStory } from '#src/components/missions/stories/RewardUnlockedStory/index.tsx';
import { useClaimTask } from '#src/components/missions/useClaimTask.tsx';

import type { AnyTaskBasicClaimStory_taskInterface } from './__generated__/index.graphql.ts';

type Props = {
  task: AnyTaskBasicClaimStory_taskInterface;
  onClose: () => void;
  showContinueButton?: boolean;
  appendedSteps?: Step[];
};
export const AnyTaskBasicClaimStory = ({
  task,
  onClose,
  showContinueButton = true,
  appendedSteps = [],
}: Props) => {
  const [
    claimTask,
    { loading: loadingClaimMutation, called: calledClaimMutation },
  ] = useClaimTask();
  const { sport } = useSportContext();
  const { isMobileApp, postMessage } = useIsMobileApp();
  const rewardConfigs = task.rewardConfigs || [];
  const alternativeRewardConfigs = task.alternativeRewardConfigs || [];
  const canChooseAlternativeRewardConfigs = alternativeRewardConfigs.length > 0;
  const shouldAutoClaim =
    !canChooseAlternativeRewardConfigs &&
    task.aasmState === ManagerProgressionTaskState.COMPLETED;
  const [rewards, setRewards] = useState<RewardUnlockedStory_reward[]>([]);

  const [signedAlternativeRewardConfigs, setSignedAlternativeRewardConfigs] =
    useState<string | undefined>();

  useEffect(() => {
    const doClaimTask = async () => {
      const { data } = await claimTask({
        variables: {
          taskId: task.id,
          sport,
        },
      });
      setRewards(data?.claimTask?.rewards || []);
    };
    if (shouldAutoClaim) {
      doClaimTask();
    }
  }, [shouldAutoClaim, claimTask, task.id, sport]);

  const extraSteps = [
    ...ladderPointsSteps(task.ladderContributions.at(0)),
    ...appendedSteps,
  ];

  const expectsSinglePackReward =
    !canChooseAlternativeRewardConfigs &&
    rewardConfigs.length === 1 &&
    !extraSteps.length &&
    isType(rewardConfigs[0], 'CardPacksRewardConfig');
  const awaitingClaim =
    !canChooseAlternativeRewardConfigs &&
    (loadingClaimMutation || !calledClaimMutation);

  if (awaitingClaim && (expectsSinglePackReward || !shouldAutoClaim)) {
    return null;
  }

  const onCloseDialog = () => {
    if (isMobileApp) {
      postMessage('taskClaimed', { taskId: task.id });
    }
    onClose();
  };

  const cardPackReward = rewards.find(isTypeFn('CardPackReward'));

  if (expectsSinglePackReward && cardPackReward) {
    return (
      <RevealPackCardsDialogFromReward
        open
        reward={cardPackReward}
        onClose={onCloseDialog}
      />
    );
  }

  return (
    <StepperDialog
      onClose={onCloseDialog}
      steps={[
        ...(canChooseAlternativeRewardConfigs
          ? [
              {
                bodyElement: ({ onNext }) => (
                  <Fragment key="choose-reward-story">
                    <ChooseYourRewardStory
                      rewardConfigs={rewardConfigs}
                      alternativeRewardConfigs={alternativeRewardConfigs}
                      setSignedAlternativeRewardConfigs={
                        setSignedAlternativeRewardConfigs
                      }
                      onConfirm={async () => {
                        const { data } = await claimTask({
                          variables: {
                            taskId: task.id,
                            signedAlternativeRewardConfigs,
                            sport,
                          },
                        });
                        setRewards(data?.claimTask?.rewards || []);
                        onNext();
                      }}
                    />
                  </Fragment>
                ),
              } as Step,
            ]
          : []),
        {
          bodyElement: ({ onNext }) => (
            <Fragment key="reward-unlocked-story">
              {awaitingClaim ? (
                <RewardUnlockedStoryLoading />
              ) : (
                <RewardUnlockedStory
                  onClose={onNext}
                  rewards={rewards}
                  fallbackRewardConfigs={
                    signedAlternativeRewardConfigs
                      ? alternativeRewardConfigs[0].rewardConfigs
                      : rewardConfigs
                  }
                  showContinueButton={showContinueButton}
                />
              )}
            </Fragment>
          ),
        },
        ...extraSteps,
      ]}
    />
  );
};

AnyTaskBasicClaimStory.fragments = {
  taskInterface: gql`
    fragment AnyTaskBasicClaimStory_taskInterface on TaskInterface {
      id
      aasmState
      ladderContributions {
        id
        ...LadderPointsStory_ladderContribution
      }
      rewardConfigs {
        id
        ...ChooseYourRewardStory_rewardConfig
        ...RewardUnlockedStory_rewardConfig
      }
      alternativeRewardConfigs {
        ...ChooseYourRewardStory_alternativeRewardConfig
        rewardConfigs {
          id
          ...RewardUnlockedStory_rewardConfig
        }
      }
    }
    ${ChooseYourRewardStory.fragments.rewardConfig}
    ${ChooseYourRewardStory.fragments.alternativeRewardConfig}
    ${RewardUnlockedStory.fragments.anyRewardConfigInterface}
    ${ladderPointsSteps.fragments.ladderContribution}
  ` as TypedDocumentNode<AnyTaskBasicClaimStory_taskInterface>,
};
